import { Trans, useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'

import SectionProgress from '~/pages/anatomyV2/libs/components/SectionProgress'
import { useMatchHeaderBackground } from '~/pages/anatomyV2/libs/hooks/useMatchHeaderBackground'
import {
  EXPLORED_ZONES_COUNT,
  EXPLORE_ZONES,
  SECTION_NUMBER,
  THIRTY_DAYS_MS,
  exploreBodyFemalePNG,
  exploreBodyFemaleWEBP,
  exploreBodyMalePNG,
  exploreBodyMaleWEBP,
} from '~/pages/anatomyV2/whatWeExplored/libs/components/AnatomyV2WhatWeExplored/constants'
import { AnatomyV2WhatWeExploredStyles as S } from '~/pages/anatomyV2/whatWeExplored/libs/components/AnatomyV2WhatWeExplored/styles'
import { PageId } from '~/shared/constants/pagesConstants'
import { useNextStep } from '~/shared/hooks/useNextStep'
import { selectAnswers } from '~/shared/store/common/selects'
import { TPageProps } from '~/shared/types/commonTypes'
import { i18nFormatters } from '~/shared/utils/i18nFormatters'

const QuestionMarkIcon = () => <S.QuestionMarkIcon>?</S.QuestionMarkIcon>

const AnatomyV2WhatWeExplored = ({ pageId, nextPagePath }: TPageProps) => {
  useMatchHeaderBackground()

  const { i18n } = useTranslation()
  const answers = useSelector(selectAnswers)

  const handleContinue = useNextStep({
    nextPagePath,
    pageId,
    pageName: pageId,
    question: 'What we explored today',
  })

  const genderAnswer = answers?.[PageId.GENDER_1]
  const isMale = genderAnswer === 'male'

  const targetDate = i18nFormatters.datetime(
    new Date(Date.now() + THIRTY_DAYS_MS),
    i18n.language,
    { dateStyle: 'long' },
  )

  return (
    <S.Root>
      <S.ContentWrapper>
        <S.ProgressWrapper>
          <SectionProgress currentSection={SECTION_NUMBER} pageId={pageId} />
        </S.ProgressWrapper>

        <S.ScrollContent>
          <S.Title>
            <Trans ns="anatomyV2" i18nKey="onboarding.whatWeExplored.title" />
          </S.Title>

          <S.Subtitle>
            <Trans
              ns="anatomyV2"
              i18nKey="onboarding.whatWeExplored.subtitle"
              values={{ count: EXPLORED_ZONES_COUNT, date: targetDate }}
              components={{ bold: <S.SubtitleBold />, br: <br /> }}
            />
          </S.Subtitle>

          <S.ZonesWrapper>
            <S.ZonesGrid>
              {EXPLORE_ZONES.map(({ key, labelI18nKey, width, percent }) => {
                const isExplored = percent !== undefined

                return (
                  <S.ZoneCard key={key} $isExplored={isExplored} $width={width}>
                    {isExplored ? (
                      <S.ZonePercent>{percent}%</S.ZonePercent>
                    ) : (
                      <QuestionMarkIcon />
                    )}
                    <S.ZoneLabel $isExplored={isExplored}>
                      <Trans ns="anatomyV2" i18nKey={labelI18nKey} />
                    </S.ZoneLabel>
                  </S.ZoneCard>
                )
              })}
            </S.ZonesGrid>
          </S.ZonesWrapper>

          <S.Legend>
            <S.LegendItem>
              <S.LegendDot $isExplored />
              <Trans
                ns="anatomyV2"
                i18nKey="onboarding.whatWeExplored.legendExplored"
              />
            </S.LegendItem>

            <S.LegendItem>
              <QuestionMarkIcon />
              <Trans
                ns="anatomyV2"
                i18nKey="onboarding.whatWeExplored.legendToExplore"
              />
            </S.LegendItem>
          </S.Legend>
        </S.ScrollContent>

        <S.BodyImageWrapper>
          <picture>
            <source
              srcSet={isMale ? exploreBodyMaleWEBP : exploreBodyFemaleWEBP}
              type="image/webp"
            />
            <S.BodyImage
              src={isMale ? exploreBodyMalePNG : exploreBodyFemalePNG}
              alt="Explored body zones"
            />
          </picture>

          <S.FadeOverlay />
        </S.BodyImageWrapper>

        <S.ButtonWrapper>
          <S.StyledButton
            type="button"
            onClick={() => handleContinue('continue')}
          >
            <Trans i18nKey="actions.continue" />
          </S.StyledButton>
        </S.ButtonWrapper>
      </S.ContentWrapper>
    </S.Root>
  )
}

export default AnatomyV2WhatWeExplored
